import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { Field, TextArea, TextInput } from '@l2u/ui/components/Field.tsx';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { useConfirm } from '../lib/confirm.tsx';
import { questionsDeletedByRemoving } from './categoryRemoval.ts';
import type {
  Question,
  QuestionCategory,
  Questionnaire,
  QuestionnaireAudience,
  QuestionnaireSendMode,
} from '@l2u/ui/lib/types.ts';

type Draft = {
  name: string;
  description: string;
  audience: QuestionnaireAudience;
  contractor_types: string[];
  category_ids: string[];
  is_active: boolean;
  send_mode: QuestionnaireSendMode;
  display_as_own_tab: boolean;
};

function emptyDraft(): Draft {
  return {
    name: '',
    description: '',
    audience: 'company',
    contractor_types: [],
    category_ids: [],
    is_active: true,
    send_mode: 'on_invite',
    display_as_own_tab: false,
  };
}

export function AdminQuestionnaires() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { settings } = usePortalSettings();
  const contractorTypeOptions = settings.contractorTypeOptions ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires'),
  });

  const [editing, setEditing] = useState<Questionnaire | null>(null);
  const [creating, setCreating] = useState(false);
  // Surfaces failures from the row actions (delete/duplicate). Without this the
  // mutations rejected silently — clicking Delete appeared to do nothing.
  const [error, setError] = useState<unknown>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/questionnaires/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questionnaires'] }),
    onError: setError,
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adminApi.post(`/api/questionnaires/${id}/duplicate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
      // Duplicate clones categories into new global ones — refresh the category
      // cache so they appear without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: setError,
  });

  if (isLoading) return <CenteredSpinner />;

  const list = data?.questionnaires ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Questionnaires</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create separate questionnaires for different audiences and contractor types. Each
            questionnaire has its own categories and questions.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New questionnaire</Btn>
      </div>

      <ErrorMessage error={error} />

      {list.length === 0 ? (
        <EmptyState
          title="No questionnaires yet"
          description="Create your first questionnaire to start collecting structured responses."
        />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100">
            {list.map((q) => (
              <li key={q.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{q.name}</span>
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                      Vendor
                    </span>
                    {!q.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {q.contractor_types && q.contractor_types.length > 0
                      ? `Contractor types: ${q.contractor_types.join(', ')}`
                      : 'Applies to all contractor types'}
                  </div>
                  {q.description && (
                    <div className="mt-1 text-sm text-slate-600">{q.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <LinkBtn to={`/questions?questionnaireId=${q.id}`} size="sm">
                    Edit questions
                  </LinkBtn>
                  <Btn size="sm" variant="secondary" onClick={() => setEditing(q)}>
                    Settings
                  </Btn>
                  <Btn
                    size="sm"
                    variant="secondary"
                    loading={duplicateMutation.isPending && duplicateMutation.variables === q.id}
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Duplicate questionnaire',
                            body: (
                              <>
                                This copies <span className="font-semibold">{q.name}</span> and all
                                its questions.
                              </>
                            ),
                            confirmLabel: 'Duplicate',
                          })
                        )
                          duplicateMutation.mutate(q.id);
                      })();
                    }}
                  >
                    Duplicate
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    loading={deleteMutation.isPending && deleteMutation.variables === q.id}
                    onClick={() => {
                      void (async () => {
                        setError(null);
                        // Soft delete: the questionnaire is hidden from the list
                        // and from vendors, but its questions and submitted vendor
                        // data are preserved, so this is recoverable rather than
                        // destructive.
                        const ok = await confirm({
                          title: 'Delete questionnaire',
                          body: (
                            <>
                              Delete <span className="font-semibold">{q.name}</span>? It will be
                              removed from the list and hidden from vendors. Its questions and any
                              submitted answers are kept.
                            </>
                          ),
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (ok) deleteMutation.mutate(q.id);
                      })();
                    }}
                  >
                    Delete
                  </Btn>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating && (
        <QuestionnaireEditor
          mode="create"
          contractorTypeOptions={contractorTypeOptions}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <QuestionnaireEditor
          mode="edit"
          existing={editing}
          contractorTypeOptions={contractorTypeOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function QuestionnaireEditor({
  mode,
  existing,
  contractorTypeOptions,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: Questionnaire;
  contractorTypeOptions: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: categoriesData } = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () => adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });
  const categories = categoriesData?.categories ?? [];

  // Edit mode only: load this questionnaire's questions so an untick-to-remove
  // save can warn about (and is what server-side then deletes) the questions in
  // any category being dropped. Shares the builder's ['questions', id] cache.
  const { data: questionsData } = useQuery({
    queryKey: ['questions', existing?.id ?? 'new'],
    queryFn: () =>
      adminApi.get<{ questions: Question[] }>(`/api/questions?questionnaire_id=${existing!.id}`),
    enabled: mode === 'edit' && !!existing,
  });
  const questionnaireQuestions = questionsData?.questions ?? [];

  const [draft, setDraft] = useState<Draft>(() =>
    existing
      ? {
          name: existing.name,
          description: existing.description ?? '',
          audience: existing.audience,
          contractor_types: existing.contractor_types ?? [],
          category_ids: [],
          is_active: existing.is_active,
          send_mode: existing.send_mode ?? 'on_invite',
          display_as_own_tab: existing.display_as_own_tab ?? false,
        }
      : emptyDraft(),
  );
  const [error, setError] = useState<unknown>(null);

  // When editing, the checked categories are those whose questionnaire_ids
  // include this questionnaire. Categories load asynchronously, so derive the
  // checked set once both the questionnaire and its categories are available.
  useEffect(() => {
    if (existing) {
      setDraft({
        name: existing.name,
        description: existing.description ?? '',
        audience: existing.audience,
        contractor_types: existing.contractor_types ?? [],
        category_ids: categories
          .filter((c) => c.questionnaire_ids.includes(existing.id))
          .map((c) => c.id),
        is_active: existing.is_active,
        send_mode: existing.send_mode ?? 'on_invite',
        display_as_own_tab: existing.display_as_own_tab ?? false,
      });
    }
  }, [existing, categoriesData]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        audience: draft.audience,
        contractor_types: draft.contractor_types.length === 0 ? null : draft.contractor_types,
        category_ids: draft.category_ids,
        is_active: draft.is_active,
        send_mode: draft.send_mode,
        display_as_own_tab: draft.display_as_own_tab,
      };
      if (mode === 'create') {
        return adminApi.post('/api/questionnaires', payload);
      }
      return adminApi.patch(`/api/questionnaires/${existing!.id}`, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
      // Saving rewrites the questionnaire_categories join, which feeds each
      // category's questionnaire_ids — the source the editor derives its ticked
      // set from. Without this the cached categories stay stale (30s staleTime),
      // so reopening showed the pre-save ticks until a hard refresh. Prefix-match
      // refreshes every category query (Settings, builder, this editor).
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
      // Removing a category deletes its questions server-side; refresh the
      // builder's question cache so they disappear without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['questions'] });
      onClose();
    },
    onError: setError,
  });

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // Categories are stored as ids but shown by name in the dropdown and summary.
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? id;

  // Linked categories the user has unticked that still contain questions —
  // saving will permanently delete those questions and their vendor data.
  async function handleSave() {
    if (existing) {
      const removedWithQuestions = categories
        .filter(
          (c) => c.questionnaire_ids.includes(existing.id) && !draft.category_ids.includes(c.id),
        )
        .map((c) => ({
          name: c.name,
          count: questionsDeletedByRemoving(questionnaireQuestions, c.id).filter(
            (q) => !q.instance_of,
          ).length,
        }))
        .filter((r) => r.count > 0);

      if (removedWithQuestions.length > 0) {
        await confirm({
          title: 'Cannot remove category',
          body: (
            <div className="space-y-2">
              <p>
                These categories still have questions in this questionnaire, so they can't be
                removed here. Archive the category in Settings instead — its questions and the
                vendors' answers are kept.
              </p>
              <ul className="list-disc pl-5">
                {removedWithQuestions.map((r) => (
                  <li key={r.name}>
                    {r.name} ({r.count} question{r.count === 1 ? '' : 's'})
                  </li>
                ))}
              </ul>
            </div>
          ),
          confirmLabel: 'OK',
        });
        return; // do not save — keeps the categories linked
      }
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'New questionnaire' : `Edit ${existing!.name}`}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => void handleSave()}
            loading={saveMutation.isPending}
            disabled={!draft.name.trim()}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <TextInput value={draft.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Display">
            <div className="mt-2 flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                />
                Active
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.display_as_own_tab}
                  onChange={(e) => set('display_as_own_tab', e.target.checked)}
                />
                Show as its own tab
              </label>
            </div>
          </Field>
        </div>

        {draft.audience === 'company' && (
          <Field label="Send mode" hint="How this questionnaire reaches vendors.">
            <div className="mt-1 flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="send_mode"
                  value="on_invite"
                  checked={draft.send_mode === 'on_invite'}
                  onChange={() => set('send_mode', 'on_invite')}
                />
                Attach to invite (automatic)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="send_mode"
                  value="manual"
                  checked={draft.send_mode === 'manual'}
                  onChange={() => set('send_mode', 'manual')}
                />
                Send manually
              </label>
            </div>
          </Field>
        )}

        <Field
          label="Contractor types"
          hint="Leave empty to make this questionnaire apply to every contractor type."
          asGroup
        >
          {contractorTypeOptions.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No contractor types configured under Settings yet. Without any, this questionnaire
              applies to every contractor type.
            </div>
          ) : (
            <div className="space-y-1">
              <MultiSelect
                label="Contractor types"
                ariaLabel="Select contractor types"
                fullWidth
                values={contractorTypeOptions}
                selected={draft.contractor_types}
                onChange={(next) => set('contractor_types', next)}
              />
              <div className="text-xs text-slate-600">
                {draft.contractor_types.length > 0 ? (
                  <>
                    Selected:{' '}
                    <span className="font-medium text-slate-800">
                      {draft.contractor_types.join(', ')}
                    </span>
                  </>
                ) : (
                  <span className="italic text-slate-400">Applies to all contractor types</span>
                )}
              </div>
            </div>
          )}
        </Field>

        <Field
          label="Categories"
          hint="Select the question categories this questionnaire includes. Categories are global and can be shared across questionnaires."
          asGroup
        >
          {categories.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No categories configured under Settings yet. Create a category first, then come back
              to include it in this questionnaire.
            </div>
          ) : (
            <div className="space-y-1">
              <MultiSelect
                label="Categories"
                ariaLabel="Select categories"
                fullWidth
                values={categories.map((c) => c.id)}
                selected={draft.category_ids}
                labelFor={categoryName}
                onChange={(next) => set('category_ids', next)}
              />
              <div className="text-xs text-slate-600">
                {draft.category_ids.length > 0 ? (
                  <>
                    Selected:{' '}
                    <span className="font-medium text-slate-800">
                      {draft.category_ids.map(categoryName).join(', ')}
                    </span>
                  </>
                ) : (
                  <span className="italic text-slate-400">No categories selected</span>
                )}
              </div>
            </div>
          )}
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
